import React from "react";
import { projects, normalizeTech } from "../data/projects";
import { useSite } from "../SiteContext";
import { useT } from "../i18n";
import { useContent } from "../i18n/useContent";
import "./Skills.css";

// A chip is only worth making clickable if it leads somewhere, so match each
// skill against the project stacks once and let the rest render as plain text.
// Same normalisation the grid filters with, so the two never disagree about
// whether a technology has work behind it. Technology names never translate
// (see PROJECT_FIELDS in useContent.js), so this is safe to build once from
// the English data rather than re-derive per language.
const techWithWork = new Set(
  projects.flatMap((project) => project.technologies.map(normalizeTech))
);

const hasWork = (name) => techWithWork.has(normalizeTech(name));

const Skills = () => {
  const { filterByTech } = useSite();
  const t = useT();
  const { skillCategories } = useContent();
  const totalSkills = skillCategories.reduce((sum, cat) => sum + cat.skills.length, 0);

  return (
    <section id="skills" className="skills">
      <div className="container">
        <header className="masthead reveal">
          <div className="masthead-rule">
            <span className="masthead-label">{t('skills.label')}</span>
            <span className="masthead-note">{t('skills.count', { count: totalSkills })}</span>
          </div>
          <h2 className="masthead-title">{t('skills.title')}</h2>
          <p className="masthead-lede">{t('skills.lede')}</p>
        </header>

        <div className="skills-matrix">
          {skillCategories.map((category) => (
            <div
              className="skill-row reveal"
              key={category.id}
              style={{
                "--tone": `var(--c-${category.tone})`,
                "--tone-tint": `var(--c-${category.tone}-tint)`,
              }}
            >
              <div className="skill-row-head">
                <h3 className="skill-row-name">{category.name}</h3>
                <span className="skill-row-count">{category.skills.length}</span>
              </div>

              <ul className="skill-list">
                {category.skills.map((skill) => {
                  const linkable = hasWork(skill.name);

                  const inner = (
                    <>
                      <span className="skill-chip-icon" aria-hidden="true">
                        {skill.icon}
                      </span>
                      <span className="skill-chip-name">{skill.name}</span>
                      <span className="skill-chip-years">{skill.years}y</span>
                      {skill.certified && (
                        <span className="skill-chip-cert">{t('skills.certified')}</span>
                      )}
                    </>
                  );

                  return (
                    <li key={skill.name}>
                      {linkable ? (
                        <button
                          type="button"
                          className="skill-chip is-linkable"
                          onClick={() => filterByTech(skill.name)}
                          title={t('a11y.showProjectsWith', { skill: skill.name })}
                        >
                          {inner}
                        </button>
                      ) : (
                        <span className="skill-chip">{inner}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Skills;
